import {
  Battery50Icon,
  ChevronLeftIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  LockClosedIcon,
  WifiIcon,
} from '@heroicons/react/16/solid'
import { motion } from 'framer-motion'
import { type DetailedHTMLProps, useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { ButtonGroup, Icn, SIconButton, JosefButton, cn, cx } from 'tailwind-ui'
import Description from 'tailwind-ui/typeography/description'
import Heading from 'tailwind-ui/typeography/heading'
import { StringParam, useQueryParam, withDefault } from 'use-query-params'
import { PhoneSignalIcon } from '@/common/Icons/CustomIcons'
import { ScrollArea } from '../scroll-area'

function BrandingBlock({
  topOffset = 0,
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { topOffset?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={cn([
        'flex-1 lg:grid grid-cols-auto  lg:grid-cols-[320px_1fr]  lg:overflow-hidden lg:h-full',
        className,
      ])}
      {...props}
      // style={{ height: width < 768 ? 'auto' : 'h-screen' }}
    >
      {children}
    </motion.div>
  )
}

export const useDeviceSwitch = (initial: 'phone' | 'laptop' = 'phone') => {
  const [device, setDevice] = useQueryParam(
    'device',
    withDefault(StringParam, initial),
    {
      updateType: 'push',
      enableBatching: true,
      skipUpdateWhenNoChange: true,
    }
  )

  return [device, setDevice] as const
}

export function DeviceToggle({
  device,
  setDevice,
  ...rest
}: DetailedHTMLProps<React.HTMLAttributes<HTMLSpanElement>, HTMLSpanElement> & {
  device: 'laptop' | 'phone'
  setDevice: (device: 'laptop' | 'phone') => void
}) {
  return (
    <div
      className="flex h-10 bg-gray-100 dark:bg-white/10 rounded-lg p-1"
      {...rest}
    >
      <button
        type="button"
        className={cn(
          'px-3 rounded-md flex items-center gap-2 transition-all',
          device === 'phone'
            ? 'bg-white dark:bg-black text-black dark:text-white shadow-xs'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
        )}
        onClick={() => {
          setDevice('phone')
        }}
      >
        <DevicePhoneMobileIcon className="w-4 h-4" />
      </button>
      <button
        type="button"
        className={cn(
          'px-3 rounded-md flex items-center gap-2 transition-all',
          device === 'laptop'
            ? 'bg-white dark:bg-black text-black dark:text-white shadow-xs'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
        )}
        onClick={() => {
          setDevice('laptop')
        }}
      >
        <ComputerDesktopIcon className="w-4 h-4" />
      </button>
    </div>
  )
}

export function BrandingBlockLeft({
  children,
  onBackPath,
  className,
  title = '',
  subtitle = '',
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  onBackPath: string | boolean
  subtitle: string
}) {
  return (
    <ScrollArea>
      <div className={cn('flex justify-between items-start py-4 px-4 pr-2 ')}>
        <div>
          <motion.div
            initial={{ x: -50 }}
            animate={{ x: 0 }}
            exit={{ x: -50 }}
            transition={{ duration: 0.5 }}
            key={`${title}_title`}
          >
            <Heading className="truncate">{title}</Heading>
          </motion.div>
          {subtitle ? (
            <Description className="truncate">{subtitle}</Description>
          ) : null}
        </div>
      </div>
      {children}
    </ScrollArea>
  )
}

// Create a simple Circle component for the animation
function Circle({
  bg,
  borderColor,
  borderWidth,
  size,
  children,
  ...props
}: any) {
  return (
    <div
      className="rounded-full flex items-center justify-center"
      style={{
        backgroundColor: bg,
        borderColor,
        borderWidth: borderWidth || 2,
        width: size || '24px',
        height: size || '24px',
        ...props.style,
      }}
      {...props}
    >
      {children}
    </div>
  )
}

const MotionCircle = motion.create(Circle)

const variants = {
  desktop: {
    width: '100%',
    height: '100%',
  },
  mobile: {
    width: 393,
    height: 852,
  },
}

const desktopIcons = {
  hidden: { y: -50 },
  show: {
    y: 0,
    transition: {
      bounce: 0.1,
      type: 'spring',
    },
  },
}

const desktopVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,

    transition: {
      staggerChildren: 0.2,
    },
  },
}

export const BrandingBrowser: React.FC<
  { url?: string; isMobile?: boolean } & React.HTMLAttributes<HTMLDivElement>
> = ({ children, url = '', isMobile = true, className, ...rest }) => {
  return (
    <div
      className={cx(
        'h-full flex flex-col items-center',
        isMobile ? 'justify-center' : '',
        className
      )}
    >
      <motion.div
        className={cx(
          'border border-border relative overflow-hidden flex flex-col mx-auto',
          {
            'rounded-2xl h-auto': isMobile,
            'rounded-lg flex-1 w-full': !isMobile,
          }
        )}
        animate={isMobile ? 'mobile' : 'desktop'}
        initial={isMobile ? 'mobile' : 'desktop'}
        transition={{ duration: 0.25 }}
        variants={variants}
        {...rest}
      >
        <div
          className="flex justify-between items-center border-b bg-neutral-100/70 dark:bg-neutral-900/70 backdrop-blur-lg pl-5 pr-4 rounded-t-xl py-2"
          key="item"
        >
          {isMobile ? (
            <motion.p
              className="flex-1 font-semibold "
              animate="show"
              initial="hidden"
              variants={desktopIcons}
            >
              9:41
            </motion.p>
          ) : null}
          {!isMobile && (
            <motion.div
              className="flex space-x-2 "
              animate="show"
              initial="hidden"
              variants={desktopVariants}
            >
              <MotionCircle
                bg="rgb(237, 106, 94)"
                borderColor="rgb(207, 89, 78)"
                borderWidth={0.5}
                size="12px"
                variants={desktopIcons}
              />
              <MotionCircle
                bg="rgb(244, 189, 80)"
                borderColor="rgb(215, 159, 62)"
                borderWidth={0.5}
                size="12px"
                variants={desktopIcons}
              />
              <MotionCircle
                bg="#61C355"
                borderColor="#52A63D"
                borderWidth={0.5}
                size="12px"
                variants={desktopIcons}
              />
            </motion.div>
          )}
          {url ? (
            <motion.div
              className={cx(
                'flex space-x-1 rounded-md transition-all duration-200 flex-1 text-sm justify-center text-center px-6 py-1 items-center',
                {
                  'md:max-w-[400px] bg-neutral-200 dark:bg-neutral-800 color-initial':
                    !isMobile,
                  'max-w-[300px] bg-transparent color-initial': isMobile,
                }
              )}
              transition={{ duration: 1, ease: 'easeOut' }}
            >
              <LockClosedIcon className="h-3 w-3" />
              <p className="truncate line-clamp-1">
                {url.replace('https://', '').replace('.prd', '')}
              </p>{' '}
            </motion.div>
          ) : (
            <div />
          )}
          {isMobile ? (
            <motion.div
              className="justify-end items-center flex-1 flex space-x-1"
              animate="show"
              initial="hidden"
              variants={desktopVariants}
            >
              <motion.div variants={desktopIcons}>
                <Icn
                  className="dark:fill-white"
                  size="sm"
                  icon={PhoneSignalIcon}
                />
              </motion.div>
              <motion.div variants={desktopIcons}>
                <Icn size="sm" icon={WifiIcon} />
              </motion.div>
              <motion.div variants={desktopIcons}>
                <Icn size="sm" icon={Battery50Icon} />
              </motion.div>
            </motion.div>
          ) : (
            <div className="w-[52px]" />
          )}
        </div>
        <div className="overflow-y-scroll flex-1 flex">{children}</div>
      </motion.div>
    </div>
  )
}

export default BrandingBlock
